import { difference } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import { StoneXButton, StoneXCheckbox, StoneXTextField } from '../../../../StoneXMui';
import { StoneXTransferList } from '../../../../StoneXMui/StoneXTransferList/StoneXTransferList';
import {
  DAILY_POSITIONS_ALL_COLUMNS,
  DailyPositionsColumn,
  DailyPositionsReportConfiguration,
} from '../../Reports/Reports/DailyPositions/Types';
import { BaseReportSettingsPageProps } from './types';

const DEFAULT_ACTIVE_COLUMNS: DailyPositionsColumn[] = [...DAILY_POSITIONS_ALL_COLUMNS];

function isDailyPositionsColumn(column: string): column is DailyPositionsColumn {
  return DAILY_POSITIONS_ALL_COLUMNS.includes(column as DailyPositionsColumn);
}

function getUniqueKnownColumns(columns: string[] | undefined): DailyPositionsColumn[] {
  if (!columns?.length) {
    return [];
  }

  const seen = new Set<DailyPositionsColumn>();
  return columns.reduce((result, column) => {
    if (!isDailyPositionsColumn(column) || seen.has(column)) {
      return result;
    }
    seen.add(column);
    result.push(column);
    return result;
  }, [] as DailyPositionsColumn[]);
}

function parseConfiguration(configurationJson: string | null | undefined): DailyPositionsReportConfiguration {
  if (!configurationJson) {
    return {};
  }

  try {
    return JSON.parse(configurationJson) as DailyPositionsReportConfiguration;
  } catch {
    return {};
  }
}

function getInitialAvailableColumns(configuration: DailyPositionsReportConfiguration): DailyPositionsColumn[] {
  const sanitized = getUniqueKnownColumns(configuration.columns?.available);
  return sanitized.length ? sanitized : [...DAILY_POSITIONS_ALL_COLUMNS];
}

function getInitialActiveColumns(configuration: DailyPositionsReportConfiguration, availableColumns: DailyPositionsColumn[]): DailyPositionsColumn[] {
  const configured = getUniqueKnownColumns(configuration.columns?.active);
  const source = configured.length ? configured : DEFAULT_ACTIVE_COLUMNS;
  return source.filter((column) => availableColumns.includes(column));
}

export default function DailyPositionsReportSettingsPage({ report, isSaving, onDeleteReport, onUpdateReport }: BaseReportSettingsPageProps) {
  const initialConfiguration = useMemo(() => parseConfiguration(report.configuration), [report.configuration]);
  const initialAvailableColumns = useMemo(() => getInitialAvailableColumns(initialConfiguration), [initialConfiguration]);
  const initialActiveColumns = useMemo(
    () => getInitialActiveColumns(initialConfiguration, initialAvailableColumns),
    [initialConfiguration, initialAvailableColumns],
  );

  const [reportName, setReportName] = useState<string>(report.reportName);
  const [groupByCommodity, setGroupByCommodity] = useState<boolean>(initialConfiguration.behavior?.groupByCommodity ?? false);
  const [availableColumns, setAvailableColumns] = useState<DailyPositionsColumn[]>(initialAvailableColumns);
  const [unavailableColumns, setUnavailableColumns] = useState<DailyPositionsColumn[]>(difference(DAILY_POSITIONS_ALL_COLUMNS, initialAvailableColumns));
  const [activeColumns, setActiveColumns] = useState<DailyPositionsColumn[]>(initialActiveColumns);
  const [inactiveAvailableColumns, setInactiveAvailableColumns] = useState<DailyPositionsColumn[]>(difference(initialAvailableColumns, initialActiveColumns));

  useEffect(() => {
    const parsed = parseConfiguration(report.configuration);
    const currentAvailableColumns = getInitialAvailableColumns(parsed);
    const currentUnavailableColumns = difference(DAILY_POSITIONS_ALL_COLUMNS, currentAvailableColumns);
    const currentActiveColumns = getInitialActiveColumns(parsed, currentAvailableColumns);
    const currentInactiveAvailableColumns = difference(currentAvailableColumns, currentActiveColumns);

    setReportName(report.reportName);
    setGroupByCommodity(parsed.behavior?.groupByCommodity ?? false);
    setAvailableColumns(currentAvailableColumns);
    setUnavailableColumns(currentUnavailableColumns);
    setActiveColumns(currentActiveColumns);
    setInactiveAvailableColumns(currentInactiveAvailableColumns);
  }, [report]);

  function onAvailableColumnsUpdate(updatedUnavailable: DailyPositionsColumn[], updatedAvailable: DailyPositionsColumn[]) {
    const filteredActiveColumns = activeColumns.filter((column) => updatedAvailable.includes(column));
    const updatedInactiveAvailableColumns = difference(updatedAvailable, filteredActiveColumns);

    setUnavailableColumns(updatedUnavailable);
    setAvailableColumns(updatedAvailable);
    setActiveColumns(filteredActiveColumns);
    setInactiveAvailableColumns(updatedInactiveAvailableColumns);
  }

  function onActiveColumnsUpdate(updatedInactiveAvailable: DailyPositionsColumn[], updatedActive: DailyPositionsColumn[]) {
    setInactiveAvailableColumns(updatedInactiveAvailable);
    setActiveColumns(updatedActive);
  }

  function saveConfiguration() {
    const nextConfiguration: DailyPositionsReportConfiguration = {
      behavior: {
        groupByCommodity,
      },
      columns: {
        available: availableColumns,
        active: activeColumns,
      },
    };

    onUpdateReport({
      ...report,
      reportName: reportName.trim(),
      configuration: JSON.stringify(nextConfiguration),
    });
  }

  return (
    <div>
      <StoneXRow>
        <StoneXTextField value={reportName} label="Report Name" onUpdate={setReportName} />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <h3>Behavior</h3>
      </StoneXRow>

      <StoneXRow>
        <StoneXCheckbox
          label="Group rows by commodity"
          checked={groupByCommodity}
          onUpdate={setGroupByCommodity}
        />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <h3>Columns</h3>
      </StoneXRow>

      <StoneXRow align="start">
        <div style={{ flex: '1 1' }}>
          <p>Step 1: Choose which columns are available to the customer.</p>
          <StoneXTransferList
            leftList={unavailableColumns}
            leftHeader="Unavailable"
            rightList={availableColumns}
            rightHeader="Available"
            getId={(item) => item}
            getOptionLabel={(item) => item}
            onUpdate={onAvailableColumnsUpdate}
          />
        </div>
        <div style={{ flex: '1 1' }}>
          <p>Step 2: Choose which of the available columns are active by default.</p>
          <StoneXTransferList
            leftList={inactiveAvailableColumns}
            leftHeader="Inactive"
            rightList={activeColumns}
            rightHeader="Active"
            getId={(item) => item}
            getOptionLabel={(item) => item}
            onUpdate={onActiveColumnsUpdate}
          />
        </div>
      </StoneXRow>

      <StoneXRow justify="end" marginDirection="top">
        <StoneXButton variant="outlined" onClick={() => onDeleteReport(report.customerReportId)}>Delete Report</StoneXButton>
        <StoneXButton isWorking={isSaving} onClick={saveConfiguration}>Save</StoneXButton>
      </StoneXRow>
    </div>
  );
}
